import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LISTADOS, celdaTextoLargo } from './definiciones';
import { rutaDetalleTramite } from '../../utils/rutasTramite';
import TablaListado from '../../components/Listados/TablaListado';
import AccesosListados from '../../components/Listados/AccesosListados';
import { CeldasPlanilla, formatoFecha } from '../../components/PlanillaHistorica/PlanillaHistorica';
import {
    listarPlanilla, opcionesSeguimiento, actualizarFilaPlanilla,
} from '../../services/planillaHistoricaService';
import useFiltroPersistente from '../../hooks/useFiltroPersistente';
import PageHeader from '../../components/ui/PageHeader';
import ModalDetalleCobro from '../../components/Cobranzas/ModalDetalleCobro';

/**
 * El seguimiento del reajuste de ARCHIVO, editable desde la fila: es la columna que el estudio sigue
 * tocando en el Excel ("Tiene Badaro", "Jauregui para cargar"…). Queda quién y cuándo lo cambió.
 */
function SelectorSeguimiento({ fila, opciones, onCambio }) {
    const [guardando, setGuardando] = useState(false);
    const cambiar = async (e) => {
        e.stopPropagation();
        setGuardando(true);
        try {
            await actualizarFilaPlanilla(fila.id, { seguimiento: e.target.value });
            onCambio();
        } catch (err) {
            toast.error(err.response?.status === 403
                ? 'No tenés permiso para cambiar el seguimiento.'
                : 'No se pudo guardar el seguimiento.');
        } finally {
            setGuardando(false);
        }
    };
    const titulo = fila.seguimientoUsuario
        ? `Último cambio: ${fila.seguimientoUsuario}${fila.seguimientoEn ? ` el ${new Date(fila.seguimientoEn).toLocaleString('es-AR')}` : ''}`
        : 'Como venía en la planilla';
    return (
        <select
            className="form-select form-select-sm" style={{ minWidth: 150, maxWidth: 190 }}
            value={fila.seguimiento || ''} disabled={guardando} title={titulo}
            onClick={(e) => e.stopPropagation()} onChange={cambiar}
        >
            <option value="">—</option>
            {opciones.map((o) => <option key={o} value={o}>{o}</option>)}
            {fila.seguimiento && !opciones.includes(fila.seguimiento) && (
                <option value={fila.seguimiento}>{fila.seguimiento}</option>
            )}
        </select>
    );
}

/** Debajo de la fila: todo el Excel y la observación (editable). */
function DetalleFila({ fila, onCambio }) {
    const [observacion, setObservacion] = useState(fila.observacion || '');
    const [guardando, setGuardando] = useState(false);
    const guardar = async () => {
        setGuardando(true);
        try {
            await actualizarFilaPlanilla(fila.id, { observacion });
            toast.success('Observación guardada.');
            onCambio();
        } catch (err) {
            toast.error(err.response?.status === 403 ? 'No tenés permiso para editar.' : 'No se pudo guardar.');
        } finally {
            setGuardando(false);
        }
    };
    return (
        <div className="d-flex flex-wrap gap-4">
            <div style={{ flex: '2 1 420px', minWidth: 0 }}>
                <div style={{ fontSize: '0.8rem', color: '#4b5563', marginBottom: 6 }}>
                    <i className="bi bi-file-earmark-excel me-1" style={{ color: '#15803d' }} />
                    Fila {fila.nroFila} de {fila.origen === 'ARCHIVO' ? 'ARCHIVO' : 'CARPETAS ELIMINADAS'}
                </div>
                <CeldasPlanilla fila={fila} />
            </div>
            <div style={{ flex: '1 1 260px', minWidth: 0 }}>
                <label className="form-label" style={{ fontSize: '0.85rem', fontWeight: 600 }}>Observación</label>
                <textarea className="form-control form-control-sm" rows={3} value={observacion}
                          onChange={(e) => setObservacion(e.target.value)} />
                <button type="button" className="btn btn-sm btn-primary mt-2" disabled={guardando} onClick={guardar}>
                    {guardando ? 'Guardando…' : 'Guardar observación'}
                </button>
            </div>
        </div>
    );
}

/**
 * CARPETAS ELIMINADAS y ARCHIVO, las dos solapas del Excel que no son trabajo en curso: quién pasó
 * por el estudio y por qué se fue, y quién ya cobró. No son clientes del sistema (se vinculan por
 * CUIL: "ver cliente" cuando lo es). Ver PlanillaHistoricaService.
 */
export default function ListadoPlanillaHistorica({ origen }) {
    const DEF = origen === 'ARCHIVO' ? LISTADOS.archivo : LISTADOS.carpetasEliminadas;
    const esArchivo = origen === 'ARCHIVO';
    const prefijo = `listadoPlanilla.${origen}`;
    const [tramite, setTramite] = useFiltroPersistente(`${prefijo}.tramite`, '');
    const [anio, setAnio] = useFiltroPersistente(`${prefijo}.anio`, '');
    const [seguimiento, setSeguimiento] = useFiltroPersistente(`${prefijo}.seguimiento`, '');
    const [opciones, setOpciones] = useState([]);
    const [abiertas, setAbiertas] = useState(() => new Set());
    const [recarga, setRecarga] = useState(0);
    // Las filas que son cobros del sistema pasados a Archivo (V269) se abren con su detalle.
    const [idCobroAbierto, setIdCobroAbierto] = useState(null);

    useEffect(() => {
        if (!esArchivo) return;
        opcionesSeguimiento().then(setOpciones).catch(() => setOpciones([]));
    }, [esArchivo]);

    const fetchPage = useCallback(
        // El año solo filtra cuando tiene 4 cifras: mientras se tipea ("20") no busca nada.
        (page, size, texto) => listarPlanilla(origen, page, size, texto,
            { tramite, anio: String(anio).length === 4 ? anio : '', seguimiento }),
        [origen, tramite, anio, seguimiento]
    );

    const alternar = useCallback((row) => setAbiertas((previas) => {
        const nuevas = new Set(previas);
        if (nuevas.has(row.id)) nuevas.delete(row.id); else nuevas.add(row.id);
        return nuevas;
    }), []);
    const recargar = useCallback(() => setRecarga((n) => n + 1), []);

    const columnas = useMemo(() => {
        const cliente = {
            key: 'cliente', header: 'Nombre', className: 'text-start',
            render: (row) => (
                <div className="text-start" style={{ maxWidth: 230 }}>
                    <div style={{ fontWeight: 500, color: '#374151' }}>
                        {row.nombre || 'Sin nombre'}
                        {(row.idCobro || row.idTramite) && (
                            <span className="badge rounded-pill bg-primary-subtle text-primary-emphasis ms-2"
                                  title={row.idCobro ? 'Cobro del sistema pasado a Archivo' : 'Trámite del sistema cancelado'}>Sistema</span>
                        )}
                    </div>
                    <div className="d-flex gap-2" style={{ fontSize: '0.75rem' }}>
                        {row.idCobro && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); setIdCobroAbierto(row.idCobro); }}
                                    style={{ background: 'none', border: 'none', padding: 0, color: '#2563eb', cursor: 'pointer' }}>
                                <i className="bi bi-cash-coin me-1" />Ver cobro
                            </button>
                        )}
                        {row.idTramite && (
                            <Link to={rutaDetalleTramite({ idTramite: row.idTramite, nombreTipoTramite: row.nombreTipoTramite })}
                                  onClick={(e) => e.stopPropagation()}>
                                <i className="bi bi-folder2-open me-1" />Ver trámite
                            </Link>
                        )}
                        {!row.idCobro && !row.idTramite && (
                            <button type="button" onClick={(e) => { e.stopPropagation(); alternar(row); }}
                                    style={{ background: 'none', border: 'none', padding: 0, color: '#15803d', cursor: 'pointer' }}>
                                <i className="bi bi-file-earmark-excel me-1" />{abiertas.has(row.id) ? 'Ocultar fila' : 'Fila del Excel'}
                            </button>
                        )}
                        {row.esCliente && (
                            <Link to={`/clientes/vista-detallada/${row.cuil}`} onClick={(e) => e.stopPropagation()}>ver cliente</Link>
                        )}
                    </div>
                </div>
            ),
        };
        const cuil = { key: 'cuil', header: 'CUIL', render: (row) => row.cuil || <span style={{ color: '#6b7280' }}>—</span> };
        const tram = { key: 'tramite', header: 'Trámite', className: 'text-start', render: (row) => celdaTextoLargo(row.tramite, 180) };
        const fecha = {
            key: 'fecha', header: esArchivo ? 'Cobró' : 'Eliminada',
            render: (row) => <span title={row.fechaTexto || ''} style={{ whiteSpace: 'nowrap' }}>{formatoFecha(row)}</span>,
        };
        if (esArchivo) {
            return [cliente, cuil, tram, fecha,
                { key: 'banco', header: 'Banco', className: 'text-start', render: (row) => celdaTextoLargo(row.banco, 150) },
                { key: 'arreglo', header: 'Arreglo del cobro', className: 'text-start', render: (row) => celdaTextoLargo(row.arreglo, 150) },
                {
                    key: 'seguimiento', header: 'Seguimiento reajuste',
                    render: (row) => <SelectorSeguimiento fila={row} opciones={opciones} onCambio={recargar} />,
                },
            ];
        }
        return [cliente, cuil, tram, fecha,
            { key: 'motivo', header: 'Motivo', className: 'text-start', render: (row) => celdaTextoLargo(row.motivo, 280) },
            { key: 'referencia', header: 'Referencia', className: 'text-start', render: (row) => celdaTextoLargo(row.referencia, 140) },
        ];
    }, [esArchivo, opciones, abiertas, alternar, recargar]);

    const filaDetalle = useCallback(
        (row) => (abiertas.has(row.id) ? <DetalleFila fila={row} onCambio={recargar} /> : null),
        [abiertas, recargar]
    );

    const chipsExtra = [
        tramite && { key: 'tramite', label: `Trámite: ${tramite}`, onQuitar: () => setTramite('') },
        anio && { key: 'anio', label: String(anio), onQuitar: () => setAnio('') },
        seguimiento && { key: 'seguimiento', label: seguimiento === '-' ? 'Sin seguimiento' : seguimiento, onQuitar: () => setSeguimiento('') },
    ].filter(Boolean);

    return (
        <div className="container-fluid mt-4 listado-pagina area-cobranzas">
            <PageHeader
                variante="plano"
                icono={DEF.icono}
                titulo={DEF.titulo}
                descripcion={DEF.subtitulo}
            />

            <AccesosListados actual={DEF.clave} />

            <TablaListado
                claveFiltro={prefijo}
                columnas={columnas}
                fetchPage={fetchPage}
                rowKey={(row) => row.id}
                dependencias={[origen, tramite, anio, seguimiento, recarga]}
                filaDetalle={filaDetalle}
                compacto
                size={50}
                chipsExtra={chipsExtra}
                onLimpiarExtra={() => { setTramite(''); setAnio(''); setSeguimiento(''); }}
                filtrosExtra={
                    <>
                        <div>
                            <label className="form-label"><i className="bi bi-folder2-open me-2"></i>Trámite</label>
                            <input className="form-control" placeholder="JUB, PUAM, BADARO…" value={tramite}
                                   onChange={(e) => setTramite(e.target.value)} />
                        </div>
                        <div style={{ maxWidth: 130 }}>
                            <label className="form-label"><i className="bi bi-calendar me-2"></i>Año</label>
                            <input type="number" className="form-control" min="1990" max="2100" value={anio}
                                   onChange={(e) => setAnio(e.target.value)} />
                        </div>
                        {esArchivo && (
                            <div>
                                <label className="form-label"><i className="bi bi-arrow-repeat me-2"></i>Seguimiento</label>
                                <select className="form-select" value={seguimiento} onChange={(e) => setSeguimiento(e.target.value)}>
                                    <option value="">Todos</option>
                                    <option value="-">Sin seguimiento</option>
                                    {opciones.map((o) => <option key={o} value={o}>{o}</option>)}
                                </select>
                            </div>
                        )}
                    </>
                }
            />

            {esArchivo && (
                <ModalDetalleCobro
                    open={!!idCobroAbierto}
                    idCobro={idCobroAbierto}
                    onClose={() => setIdCobroAbierto(null)}
                    onCambio={recargar}
                />
            )}
        </div>
    );
}
